<template lang='pug'>
  .line-item-card
    img.image(v-if='displayedImage.length > 0' :src='displayedImage')
    .image(v-else)
    .info
      template(v-if='sample')
        h4.brand(v-t='sampleBrand')
        p.name(v-text='sampleName')
        p.price.free(v-t='"checkout.common.free"')
      template(v-else-if='product')
        h4.brand(v-text='productBrand')
        p.name(v-text='displayProductName')
        p.price.free(
          v-t='"checkout.common.free"'
          v-if='isPriceZero'
        )
        p.price(
          v-text='productPrice'
          v-else
        )
      p.variant-info
        span(v-t='"products.quantity"')
        span.quantity(v-text='lineItem.attributes ? lineItem.attributes.quantity : lineItem.quantity')
        span.variant-name(v-if='displayedVariantName.length > 0' v-text='displayedVariantName')
        a.remove-line-item(
          v-if='displayRemoveLineItem'
          href='#'
          @click.prevent='removeFromCart'
          v-t='"buttons.remove"'
        )
</template>

<script>
import { mapGetters } from 'vuex'
import get from 'lodash/get'
import { notifyBugsnag } from '@/modules/services/bugsnag'
import { pushDataLayerEvent } from '@/modules/services/gtm'
import { humanizePrice } from '@/modules/services/price-support'

export default {
  props: {
    lineItem: {
      type: Object,
      default: () => {}
    },
    product: {
      type: Object,
      default: () => {}
    },
    sample: {
      type: Object,
      default: () => {}
    },
    variant: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    ...mapGetters([
      'findBrandById'
    ]),

    sampleBrand () {
      return this.sample.attributes ? this.sample.attributes.brand : this.sample['brand-name']
    },

    sampleName () {
      return this.sample.attributes ? this.sample.attributes.name : this.sample['product-name']
    },

    productBrand () {
      if (this.lineItem['brand-name']) return this.lineItem['brand-name']
      const brandId = get(this.lineItem, 'relationships.brand.data.id') || get(this.product, 'relationships.brand.data.id')
      const brand = this.findBrandById(brandId)
      return get(brand, 'attributes.name', '')
    },

    displayProductName () {
      return this.lineItem['product-name'] || this.product.attributes?.name
    },

    productPrice () {
      return this.lineItem['display-price'] || humanizePrice(this.lineItem.attributes?.price)
    },

    isPriceZero () {
      const displayPrice = this.lineItem['display-price']
      if (displayPrice) {
        return parseFloat(displayPrice.replace(/[^\d.]/g, '')) === 0
      }
      return this.lineItem.attributes?.price === 0
    },

    displayedVariantName () {
      const variantName = this.lineItem['variant-name'] || get(this.variant, 'attributes.name', 'Default')
      if (variantName === 'Default') { return '' }
      return variantName
    },

    displayedImage () {
      if (this.lineItem['image-url']) return this.lineItem['image-url']
      const variant = get(this.variant, 'attributes')

      if (variant) {
        return variant['cart-image-url'] || variant['cart-image-urls'][0] || variant['image-urls'][0]
      } else {
        return ''
      }
    },

    displayRemoveLineItem () {
      const type = this.lineItem.attributes ? this.lineItem.attributes['line-item-type'] : this.lineItem.type
      return type !== 'gwp' && type !== 'free_sample'
    }
  },

  methods: {
    async removeFromCart () {
      // i18n gets destroyed as the component is removed, so the translation should be stored beforehand
      const message = this.$t('notifications.error')

      try {
        await this.$store.dispatch('removeLineItem', this.lineItem.id)
        try {
          this.updateDataLayer()
        } catch (error) {
          notifyBugsnag(error)
        }
      } catch (error) {
        notifyBugsnag(error)
        this.$store.commit('setNotification', { msg: message, type: 'error' })
      }
    },

    updateDataLayer () {
      const quantity = -(this.lineItem.quantity || this.lineItem.attributes?.quantity || 1)
      const newDataLayerCartProducts = dataLayer[0].cart.products.filter(product => product.variant_id !== parseInt(this.variant.id))
      const newDataLayerCartVariantId = dataLayer[0].cart.productid_cart.split(',').filter(id => id !== this.variant.id)

      dataLayer[0].cart.products = newDataLayerCartProducts
      dataLayer[0].cart.productid_cart = newDataLayerCartVariantId.join(',')

      pushDataLayerEvent('Remove from Cart', {
        product_id: this.product.id,
        product_name: this.product.attributes.name,
        variant_id: this.variant.id,
        brand: this.variant.attributes['brand-name'],
        price: this.variant.attributes.price,
        quantity: quantity
      })
    }
  }
}
</script>

<style lang='scss' scoped>
  .line-item-card {
    padding-top: 16px;
    padding-bottom: 3px;

    .image {
      float: left;
      margin-right: 20px;
      width: 90px;
      height: 90px;
    }

    .info {
      font-size: $font-size-root;

      .brand {
        text-transform: uppercase;
        text-align: left;
        margin-bottom: 0;
        @include font-weight-bold;
      }

      p {
        @include font-weight-book;

        span {
          margin-right: 10.5px;
          margin-left: -5.5px;
          padding-left: 6px;

          &:nth-child(3)::before {
            content: '';
            border-left: 1px solid $grey;
            padding-top: 20px;
            padding-bottom: 3px;
            margin-left: -3px;
            margin-right: 9px;
          }
        }
      }

      .price {
        @include font-weight-medium;

        &.free {
          text-transform: uppercase;
        }
      }

      .variant-info {
        white-space: nowrap;
        text-overflow: ellipsis;
        display: block;
        overflow: hidden;
        margin-top: 21px;
        margin-bottom: 5px;
        text-align-last: left;
        margin-left: 111px;
      }
    }

    .remove-line-item {
      @include font-weight-bold;
      font-size: 14px;
      float: right;
      margin-top: 16px;
    }
  }
</style>
